import './App.css'

import { Analytics } from '@vercel/analytics/react'
import confetti from 'canvas-confetti'
import { default as GraphemeSplitter } from 'grapheme-splitter'
import { useEffect, useState } from 'react'
import Div100vh from 'react-div-100vh'

import { AlertContainer } from './components/alerts/AlertContainer'
import { Grid } from './components/grid/Grid'
import { Keyboard } from './components/keyboard/Keyboard'
import Banner from './components/misc/Banner'
import { InfoModal } from './components/modals/InfoModal'
import { MigrateStatsModal } from './components/modals/MigrateStatsModal'
import { RetryOrRevealModal } from './components/modals/RetryOrRevealModal'
import { SettingsModal } from './components/modals/SettingsModal'
import { StatsModal } from './components/modals/StatsModal'
import { Navbar } from './components/navbar/Navbar'
import {
  LONG_ALERT_TIME_MS,
  MAX_CHALLENGES,
  REVEAL_TIME_MS,
  WELCOME_INFO_MODAL_MS,
} from './constants/settings'
import {
  CORRECT_WORD_MESSAGE,
  GAME_COPIED_MESSAGE,
  HARD_MODE_ALERT_MESSAGE,
  NOT_ENOUGH_LETTERS_MESSAGE,
  SHARE_FAILURE_TEXT,
  WIN_MESSAGES,
  WORD_NOT_FOUND_MESSAGE,
} from './constants/strings'
import { useAlert } from './context/AlertContext'
import {
  getStoredIsHighContrastMode,
  loadGameStateFromLocalStorage,
  saveGameStateToLocalStorage,
  setStoredIsHighContrastMode,
} from './lib/localStorage'
import { addStatsForCompletedGame, loadStats } from './lib/stats'
import {
  findFirstUnusedReveal,
  getSolution,
  isWordInWordList,
  solution,
  unicodeLength,
} from './lib/words'

const NUM_RETRIES = 1

export const THEMES = { light: 'light', dark: 'dark', colorful: 'colorful' }

function App() {
  const isLatestGame = true

  const { showError: showErrorAlert, showSuccess: showSuccessAlert } =
    useAlert()
  const [currentGuess, setCurrentGuess] = useState('')
  const [isGameWon, setIsGameWon] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false)
  const [revealSolution, setRevealSolution] = useState(false)
  const [gameSolution, setGameSolution] = useState('')
  const [shouldRetry, setShouldRetry] = useState(false)
  const [numRetries, setNumRetries] = useState(0)
  const [isRetryOrRevealModalOpen, setIsRetryOrRevealModalOpen] =
    useState(false)
  const [isMigrateStatsModalOpen, setIsMigrateStatsModalOpen] = useState(false)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const [currentRowClass, setCurrentRowClass] = useState('')
  const [isGameLost, setIsGameLost] = useState(false)
  const [theme, setTheme] = useState(localStorage.getItem('theme') ?? 'light')
  const [isHighContrastMode, setIsHighContrastMode] = useState(
    getStoredIsHighContrastMode()
  )
  const [isRevealing, setIsRevealing] = useState(false)
  const [guesses, setGuesses] = useState<string[]>(() => {
    const loaded = loadGameStateFromLocalStorage(isLatestGame)
    if (loaded?.solution !== gameSolution) {
      return []
    }
    const gameWasWon = loaded.guesses.includes(gameSolution)
    if (gameWasWon) {
      setIsGameWon(true)
    }
    if (loaded.guesses.length === MAX_CHALLENGES && !gameWasWon) {
      setIsGameLost(true)
      if (numRetries < NUM_RETRIES) {
        setTimeout(() => {
          setIsRetryOrRevealModalOpen(true)
        }, 1000)
      } else {
        showErrorAlert(CORRECT_WORD_MESSAGE(gameSolution), {
          persist: true,
        })
      }
    }
    return loaded.guesses
  })

  const fireConfetti = () => {
    const defaults = {
      origin: { y: 0, x: 0.5 },
      particleCount: 100,
      spread: 70,
      startVelocity: 30,
      gravity: 1.2,
      ticks: 200,
    }

    confetti({
      ...defaults,
      angle: 60,
      origin: { x: 0, y: 0 },
    })
    confetti({
      ...defaults,
      angle: 120,
      origin: { x: 1, y: 0 },
    })
  }

  const [stats, setStats] = useState(() => loadStats())

  const [isHardMode, setIsHardMode] = useState(
    localStorage.getItem('gameMode')
      ? localStorage.getItem('gameMode') === 'hard'
      : false
  )

  useEffect(() => {
    setGameSolution(solution)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isGameLost && !isRetryOrRevealModalOpen && revealSolution) {
      showErrorAlert(CORRECT_WORD_MESSAGE(gameSolution), {
        persist: true,
      })
      setTimeout(
        () => {
          setIsStatsModalOpen(true)
        },
        gameSolution.length * REVEAL_TIME_MS + 150
      )
    }
  }, [
    revealSolution,
    gameSolution,
    isGameLost,
    isRetryOrRevealModalOpen,
    showErrorAlert,
  ])

  useEffect(() => {
    if (numRetries >= NUM_RETRIES) {
      setTimeout(
        () => {
          setIsStatsModalOpen(true)
        },
        gameSolution.length * REVEAL_TIME_MS + 150
      )
      return
    }
    if (shouldRetry) {
      setNumRetries(numRetries + 1)
      resetGame()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRetry, numRetries, gameSolution.length])

  useEffect(() => {
    // if no game state on load,
    // show the user the how-to info modal
    if (!loadGameStateFromLocalStorage(true)) {
      setTimeout(() => {
        setIsInfoModalOpen(true)
      }, WELCOME_INFO_MODAL_MS)
    }
  })

  useEffect(() => {
    document.documentElement.classList.remove('dark')
    document.documentElement.classList.remove('colorful')
    switch (theme) {
      case 'dark':
        document.documentElement.classList.add('dark')
        break
      case 'colorful':
        document.documentElement.classList.add('colorful')
        break
      case 'light':
        break
      default:
    }
  }, [theme])

  useEffect(() => {
    if (isHighContrastMode) {
      document.documentElement.classList.add('high-contrast')
    } else {
      document.documentElement.classList.remove('high-contrast')
    }
  }, [isHighContrastMode])

  const handleThemeChange = (theme: string) => {
    setTheme(theme)
    localStorage.setItem('theme', theme)
  }

  const handleHardMode = (isHard: boolean) => {
    if (guesses.length === 0 || localStorage.getItem('gameMode') === 'hard') {
      setIsHardMode(isHard)
      localStorage.setItem('gameMode', isHard ? 'hard' : 'normal')
    } else {
      showErrorAlert(HARD_MODE_ALERT_MESSAGE)
    }
  }

  const resetGame = () => {
    // Reset state, keeping the solution the same. Show an empty alert to get rid of other alerts (hacky I know :( )
    showSuccessAlert('', { durationMs: 0 })
    setIsGameLost(false)
    setIsGameWon(false)
    setCurrentGuess('')
    setGuesses([])
    setIsInfoModalOpen(false)
    setIsStatsModalOpen(false)
    setRevealSolution(false)
    setIsRetryOrRevealModalOpen(false)
    setIsMigrateStatsModalOpen(false)
    setIsSettingsModalOpen(false)
    setCurrentRowClass('')
    setIsRevealing(false)
    return
  }

  const newGame = () => {
    // Get new word and reset game
    const { solution: newSolution } = getSolution()
    setGameSolution(newSolution)
    setNumRetries(0)
    setShouldRetry(false)
    resetGame()
  }

  const handleHighContrastMode = (isHighContrast: boolean) => {
    setIsHighContrastMode(isHighContrast)
    setStoredIsHighContrastMode(isHighContrast)
  }

  const clearCurrentRowClass = () => {
    setCurrentRowClass('')
  }

  useEffect(() => {
    saveGameStateToLocalStorage(true, { guesses, solution: gameSolution })
  }, [guesses, gameSolution])

  useEffect(() => {
    if (isGameWon) {
      const winMessage =
        WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]
      const delayMs = REVEAL_TIME_MS * gameSolution.length

      showSuccessAlert(winMessage, {
        delayMs,
        onClose: () => setIsStatsModalOpen(true),
      })

      setTimeout(() => {
        fireConfetti()
      }, delayMs)
    }

    if (
      isGameLost &&
      !isRetryOrRevealModalOpen &&
      (revealSolution || numRetries >= NUM_RETRIES)
    ) {
      setTimeout(
        () => {
          setIsStatsModalOpen(true)
        },
        (gameSolution.length + 1) * REVEAL_TIME_MS + 150
      )
    }
  }, [
    isGameWon,
    isGameLost,
    showSuccessAlert,
    gameSolution.length,
    isRetryOrRevealModalOpen,
    revealSolution,
    numRetries,
  ])

  const onChar = (value: string) => {
    const currentWord = `${currentGuess}${value}`
    if (
      unicodeLength(currentWord) <= gameSolution.length &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setCurrentGuess(currentWord)
    }

    if (
      currentWord.length === gameSolution.length &&
      !isWordInWordList(currentWord)
    ) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(WORD_NOT_FOUND_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }
  }

  const onDelete = () => {
    setCurrentGuess(
      new GraphemeSplitter().splitGraphemes(currentGuess).slice(0, -1).join('')
    )
  }

  const onEnter = () => {
    if (isGameWon || isGameLost) {
      return
    }

    if (!(unicodeLength(currentGuess) === gameSolution.length)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(NOT_ENOUGH_LETTERS_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    if (!isWordInWordList(currentGuess)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(WORD_NOT_FOUND_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    // enforce hard mode - all guesses must contain all previously revealed letters
    if (isHardMode) {
      const firstMissingReveal = findFirstUnusedReveal(
        currentGuess,
        guesses,
        gameSolution
      )
      if (firstMissingReveal) {
        setCurrentRowClass('jiggle')
        return showErrorAlert(firstMissingReveal, {
          onClose: clearCurrentRowClass,
        })
      }
    }

    setIsRevealing(true)
    // turn this back off after all
    // chars have been revealed
    setTimeout(() => {
      setIsRevealing(false)
    }, REVEAL_TIME_MS * gameSolution.length)

    const winningWord = gameSolution === currentGuess

    if (
      unicodeLength(currentGuess) === gameSolution.length &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setGuesses([...guesses, currentGuess])
      setCurrentGuess('')

      if (winningWord) {
        if (isLatestGame && numRetries === 0) {
          setStats(addStatsForCompletedGame(stats, guesses.length))
        }
        return setIsGameWon(true)
      }

      if (guesses.length === MAX_CHALLENGES - 1) {
        if (isLatestGame && numRetries === 0) {
          setStats(addStatsForCompletedGame(stats, guesses.length + 1))
        }
        setIsGameLost(true)
        if (numRetries < NUM_RETRIES) {
          setTimeout(() => {
            setIsRetryOrRevealModalOpen(true)
          }, 2100)
        } else {
          showErrorAlert(CORRECT_WORD_MESSAGE(gameSolution), {
            persist: true,
            delayMs: REVEAL_TIME_MS * gameSolution.length + 1,
          })
          setTimeout(
            () => {
              setIsStatsModalOpen(true)
            },
            gameSolution.length * REVEAL_TIME_MS + 150
          )
        }
      }
    }
  }

  return (
    <Div100vh>
      <div className="flex h-full flex-col">
        <Banner />
        <Navbar
          setIsInfoModalOpen={setIsInfoModalOpen}
          setIsStatsModalOpen={setIsStatsModalOpen}
          setIsSettingsModalOpen={setIsSettingsModalOpen}
          theme={theme}
        />
        <div className="mx-auto flex w-full grow flex-col px-1 pb-8 pt-2 sm:px-6 md:max-w-7xl lg:px-8 short:pb-2 short:pt-2">
          <div className="flex grow flex-col justify-center pb-6 short:pb-2">
            <Grid
              solution={gameSolution}
              guesses={guesses}
              currentGuess={currentGuess}
              isRevealing={isRevealing}
              currentRowClassName={currentRowClass}
              theme={theme}
            />
          </div>
          <Keyboard
            onChar={onChar}
            onDelete={onDelete}
            onEnter={onEnter}
            solution={gameSolution}
            guesses={guesses}
            isRevealing={isRevealing}
            theme={theme}
          />
          <InfoModal
            isOpen={isInfoModalOpen}
            handleClose={() => setIsInfoModalOpen(false)}
          />
          <RetryOrRevealModal
            isOpen={isRetryOrRevealModalOpen}
            handleClose={() => setIsRetryOrRevealModalOpen(false)}
            setReveal={() => setRevealSolution(true)}
            setRetry={() => setShouldRetry(true)}
          />
          <StatsModal
            isOpen={isStatsModalOpen}
            handleClose={() => setIsStatsModalOpen(false)}
            solution={gameSolution}
            guesses={guesses}
            gameStats={stats}
            isLatestGame={isLatestGame}
            isGameLost={isGameLost}
            isGameWon={isGameWon}
            handleShareToClipboard={() => showSuccessAlert(GAME_COPIED_MESSAGE)}
            handleShareFailure={() =>
              showErrorAlert(SHARE_FAILURE_TEXT, {
                durationMs: LONG_ALERT_TIME_MS,
              })
            }
            isHardMode={isHardMode}
            theme={theme}
            numberOfGuessesMade={guesses.length}
            newGame={() => newGame()}
          />
          <MigrateStatsModal
            isOpen={isMigrateStatsModalOpen}
            handleClose={() => setIsMigrateStatsModalOpen(false)}
          />
          <SettingsModal
            isOpen={isSettingsModalOpen}
            handleClose={() => setIsSettingsModalOpen(false)}
            isHardMode={isHardMode}
            handleHardMode={handleHardMode}
            theme={theme}
            handleThemeChange={handleThemeChange}
            isHighContrastMode={isHighContrastMode}
            handleHighContrastMode={handleHighContrastMode}
            handleMigrateStatsButton={() => {
              setIsStatsModalOpen(false)
              setIsMigrateStatsModalOpen(true)
            }}
          />
          <AlertContainer />
        </div>
      </div>
      <Analytics />
    </Div100vh>
  )
}

export default App
